import { Link } from 'react-router-dom';
import Seo from '@/components/layout/Seo';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { site } from '@/mocks/columns';

const members = [
  {
    name: '阿刀',
    role: '主编 · 深扒档案主笔',
    intro: '曾是深度报道栏目编辑，习惯把一条新闻当成一份待拆的卷宗。相信慢工出细活，深扒的第一要义是耐得住枯燥。',
  },
  {
    name: '罐头',
    role: '栏目编辑 · 幕后再探主笔',
    intro: '关注内容生产与行业机制，喜欢钻进后台看流程。主张"理解了机器，才能不被机器牵着走"。',
  },
  {
    name: '老烟',
    role: '评论编辑 · 脉络专栏主笔',
    intro: '负责时间线与三方声音的梳理，自称"编辑部里最较真时间顺序的人"。口头禅：先辨信源，再理时间。',
  },
  {
    name: '灯下',
    role: '记者 · 看料方法论主笔',
    intro: '负责信源核实与核查清单，最擅长拆穿断章取义的截图。相信真相经得起放大镜，谣言怕被追问。',
  },
];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于看料社',
  url: `${site.domain}/guanyu`,
  inLanguage: 'zh-CN',
  description: '了解看料社编辑部的定位、发展理念、内容原则与团队成员。',
  mainEntity: {
    '@type': 'Organization',
    name: '看料社',
    email: site.email,
    url: site.domain,
  },
};

export default function About() {
  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title="关于看料社 · 编辑部定位/发展理念/内容原则"
        description="了解看料社：一家以编辑部为主导的深度内容平台，坚持慢比快重要、求证比站队重要、独立于流量的内容理念，汇聚深扒档案、脉络专栏、幕后再探与看料方法论。"
        keywords="关于看料社,看料社编辑部,内容理念,内容原则,深扒与专栏分析,小编深扒"
        canonical={`${site.domain}/guanyu`}
        jsonLd={jsonLd}
      />
      <Header />

      <main>
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-20">
            <nav className="text-xs text-foreground-500" aria-label="面包屑">
              <ol className="flex flex-wrap items-center gap-1">
                <li>
                  <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
                </li>
                <li aria-hidden="true" className="text-foreground-400">/</li>
                <li className="text-foreground-800" aria-current="page">关于我们</li>
              </ol>
            </nav>
            <div className="mt-8 grid grid-cols-1 items-end gap-8 lg:grid-cols-2">
              <div>
                <p className="lede-mark text-xs font-semibold uppercase tracking-[0.22em] text-primary-600">
                  About · 关于看料社
                </p>
                <h1 className="mt-3 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-5xl">
                  一家把"看料"
                  <br />
                  当成专业来做的编辑部。
                </h1>
              </div>
              <p className="max-w-xl text-base leading-relaxed text-foreground-600">
                {site.intro}
              </p>
            </div>
          </div>
        </section>

        {/* 定位与理念 */}
        <section className="border-b border-background-200">
          <div className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-18">
            <div className="mb-8 max-w-2xl">
              <p className="lede-mark text-xs font-semibold uppercase tracking-[0.22em] text-primary-600">
                Values
              </p>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                我们信奉三条朴素的价值观
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              {[
                { t: '慢，比快更重要', d: '热点只有十几分钟的生命，但脉络要用几天来梳理。我们宁可晚半天发文，也要把时间线排准确。', ic: 'ri-time-line' },
                { t: '求证，比站队更重要', d: '我们不替任何人"赢下争论"，只努力让每一条能被证实的信息都被证实，让推测都被明确标注为推测。', ic: 'ri-search-eye-line' },
                { t: '独立于流量之外', d: '我们不追逐每一场情绪化狂欢，也尽量避免被单方面信息牵着走。独立思考的前提，是保持一点距离感。', ic: 'ri-compass-3-line' },
              ].map((v) => (
                <div key={v.t} className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${v.ic} text-xl`} aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{v.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 内容原则 */}
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-18">
            <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
              <div className="max-w-2xl">
                <p className="lede-mark text-xs font-semibold uppercase tracking-[0.22em] text-primary-600">
                  Principles
                </p>
                <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  我们写深扒时的四条底线
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { n: '①', t: '先分层，再发言', d: '事实层、情绪层、动机层分开讲，绝不把三者的边界模糊掉。' },
                { n: '②', t: '能核实才写实', d: '无法证实的推测，一律标注为"可能性"，绝不伪装成既定结论。' },
                { n: '③', t: '不消费真实伤害', d: '涉及真实个体的内容，克制描述，不渲染隐私，不把伤害当流量。' },
                { n: '④', t: '保留追溯空间', d: '关键论断注明依据来源，欢迎读者提供更多一手线索共同求证。' },
              ].map((p) => (
                <div key={p.t} className="border-t-2 border-primary-600 bg-background-50 p-5">
                  <span className="font-heading text-lg font-black text-accent-600">{p.n}</span>
                  <h3 className="mt-2 font-heading text-base font-bold text-foreground-950">{p.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 编辑部成员 */}
        <section className="border-b border-background-200">
          <div className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-18">
            <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="lede-mark text-xs font-semibold uppercase tracking-[0.22em] text-primary-600">
                  Team
                </p>
                <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  编辑部成员
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {members.map((m) => (
                <div
                  key={m.name}
                  className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 font-heading text-lg font-bold text-accent-800">
                      {m.name.slice(0, 1)}
                    </span>
                    <div>
                      <h3 className="font-heading text-base font-bold text-foreground-950">{m.name}</h3>
                      <p className="text-xs text-secondary-700">{m.role}</p>
                    </div>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-foreground-600">{m.intro}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA + 联系信息 */}
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-4 py-14 md:px-6 lg:grid-cols-2">
            <div>
              <p className="lede-mark text-xs font-semibold uppercase tracking-[0.22em] text-primary-600">
                Join Us
              </p>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                一起把"看料"做扎实
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                你可以来信提供深扒线索、行业观察，或就已有栏目文章补充不同视角。编辑部每天都会处理来信，线索一经采纳会按规范核实并标注来源。
              </p>
              <div className="mt-6 space-y-3 text-sm">
                <a
                  href={`mailto:${site.email}`}
                  className="flex items-center gap-3 text-foreground-800 transition-colors hover:text-primary-600"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-mail-line" aria-hidden="true" />
                  </span>
                  {site.email}
                </a>
                <p className="flex items-center gap-3 text-foreground-800">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-message-3-line" aria-hidden="true" />
                  </span>
                  编辑部通常会在 1-2 个工作日内回复来信
                </p>
              </div>
            </div>
            <div className="flex flex-col justify-center rounded-lg border border-background-200 bg-background-50 p-7 md:p-9">
              <h3 className="font-heading text-xl font-bold text-foreground-950">想聊聊？给我们写信</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                欢迎使用站内的联系表单，描述你的线索或建议，我们会尽快查看。
              </p>
              <Link
                to="/lianxi"
                className="mt-5 inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                前往联系编辑部
                <i className="ri-arrow-right-line ml-2 text-sm" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}